import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME, LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Database, LogOut, Search, Plus, LayoutDashboard } from "lucide-react";
import { AdminTable } from "@/components/admin/AdminTable";
import { AddRowDialog } from "@/components/admin/AddRowDialog";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { t } from "@/lib/i18n";

const OVERVIEW = "__overview";

function CollectionList({ items, active, onPick }) {
  return (
    <nav className="space-y-1" data-testid="admin-collections-list">
      {items.map((c) => (
        <button key={c.name} onClick={() => onPick(c.name)} data-testid={`admin-collection-${c.name}`}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${active === c.name ? "bg-primary text-primary-foreground" : "hover:bg-slate-800 text-slate-300"}`}>
          <span className="flex items-center gap-2"><Database className="h-3.5 w-3.5" />{c.name}</span>
          <span className="text-xs font-mono-j opacity-70">{c.count}</span>
        </button>
      ))}
    </nav>
  );
}

export default function AdminPanel() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [active, setActive] = useState(OVERVIEW);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const { data: collections = [] } = useQuery({
    queryKey: ["admin-collections"],
    queryFn: async () => (await api.get("/admin/collections")).data,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["admin-rows", active, q],
    queryFn: async () => (await api.get(`/admin/collections/${active}`, { params: { q: q || undefined, limit: 500 } })).data,
    enabled: active !== OVERVIEW,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-rows"] });
    qc.invalidateQueries({ queryKey: ["admin-collections"] });
  };
  const update = useMutation({
    mutationFn: async ({ id, patch }) => (await api.put(`/admin/collections/${active}/${encodeURIComponent(id)}`, patch)).data,
    onSuccess: () => { toast.success(t("Riga aggiornata")); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/collections/${active}/${encodeURIComponent(id)}`)).data,
    onSuccess: () => { toast.success(t("Riga eliminata")); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const insert = useMutation({
    mutationFn: async (doc) => (await api.post(`/admin/collections/${active}`, doc)).data,
    onSuccess: () => { toast.success(t("Riga aggiunta")); setAddOpen(false); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-panel">
      <aside className="w-64 shrink-0 bg-slate-950 text-white p-4 flex flex-col gap-6">
        <div className="flex items-center gap-2.5">
          <img src={LOGO} alt={APP_NAME} className="h-9 w-9 rounded-lg" />
          <div>
            <p className="font-display font-extrabold leading-none">{APP_NAME}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mt-1">{t("Admin")}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <button onClick={() => setActive(OVERVIEW)} data-testid="admin-nav-overview"
            className={`w-full flex items-center gap-2 px-3 py-2 mb-4 rounded-lg text-sm font-semibold transition-colors ${active === OVERVIEW ? "bg-primary text-primary-foreground" : "hover:bg-slate-800 text-slate-200"}`}>
            <LayoutDashboard className="h-4 w-4" /> {t("Panoramica")}
          </button>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{t("Tabelle database")}</p>
          <CollectionList items={collections} active={active} onPick={(n) => { setActive(n); setQ(""); }} />
        </div>
        <Button variant="outline" onClick={doLogout} className="bg-transparent text-white border-slate-700 hover:bg-slate-800 gap-2" data-testid="admin-logout-btn">
          <LogOut className="h-4 w-4" /> {t("Esci")}
        </Button>
      </aside>

      <main className="flex-1 min-w-0 p-6 space-y-4">
        {active === OVERVIEW ? <AdminOverview /> : (<>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="admin-table-title">{active}</h1>
            <p className="text-sm text-muted-foreground">{data?.total ?? 0} {t("righe · clicca una cella per modificarla, Invio per salvare")}</p>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Cerca…")} className="pl-9 w-64 bg-white" data-testid="admin-search-input" />
            </div>
            <Button onClick={() => setAddOpen(true)} className="gap-2" data-testid="admin-add-row-btn"><Plus className="h-4 w-4" /> {t("Aggiungi riga")}</Button>
          </div>
        </div>
        <AdminTable data={data} loading={isLoading} onUpdate={(id, patch) => update.mutate({ id, patch })} onDelete={(id) => remove.mutate(id)} />
        </>)}
      </main>
      <AddRowDialog open={addOpen} onOpenChange={setAddOpen} collection={active} data={data} onSave={(doc) => insert.mutate(doc)} busy={insert.isPending} />
    </div>
  );
}
